import * as React from 'react'

import { useSiteConfig } from '@thg-commerce/enterprise-core'
import {
  Dropdown,
  DropdownOption,
  Input,
  RadioGroup,
  RatingSize,
  TextArea,
} from '@thg-commerce/gravity-elements'
import { FeatureRating } from '@thg-commerce/gravity-elements/Product/ProductReview/FeatureRating'
import { StarRatingPicker } from '@thg-commerce/gravity-elements/Product/ProductReview/StarRatingPicker'
import { FormItem } from '@thg-commerce/gravity-patterns/Form'
import { FormFieldProps } from '@thg-commerce/gravity-patterns/Form/utils'
import { spacing, styled, Text } from '@thg-commerce/gravity-theme'

import { AddReviewFormResult } from '../../../../pages/product/addReview'
import { StarRatingRadioInput } from '../StarRatingRadioInput'

export type I18nFieldsText = {
  reviewRating: string
  reviewNickname: string
  reviewTitle: string
  reviewYourReview: string
  reviewGender?: string
  reviewGenderPlaceholder?: string
  reviewAgeRange: string
  reviewAgeRangePlaceholder: string
  reviewTaste?: string
  reviewMixability?: string
  reviewEffectiveness?: string
  reviewGoesWith?: string
}

const StyledTextArea = styled(TextArea)`
  ${(props) =>
    Text(props.theme.formgroup.input.scale, props.theme.formgroup.input.style)}
`

const StyledRadioGroup = styled(RadioGroup)`
  border-radius: 3px;
  margin-bottom: ${spacing(4)};
`

const StyledFeatureRating = styled(FeatureRating)`
  margin-bottom: ${spacing(3)};
`

export const AddReviewFormFields = (
  reviewFormFields: AddReviewFormResult['addReviewForm']['reviewForm']['fields'],
  i18nText: I18nFieldsText,
): FormFieldProps[] => {
  const choosePlaceholder = new Map<string, string>([
    ['gender', i18nText.reviewGenderPlaceholder || ''],
    ['ageRange', i18nText.reviewAgeRangePlaceholder],
  ])

  const {
    enableReviewFormFeatureRatings,
    enableOrderReviewsFeatureRatings,
    useSingleStarRatings,
  } = useSiteConfig()

  const chooseLabel = new Map<string, string>([
    ['score', i18nText.reviewRating],
    ['nickname', i18nText.reviewNickname],
    ['synopsis', i18nText.reviewTitle],
    ['reviewContent', i18nText.reviewYourReview],
    ['ageRange', i18nText.reviewAgeRange],
    ['gender', i18nText.reviewGender || ''],
    ['tasteScore', i18nText.reviewTaste || ''],
    ['mixabilityScore', i18nText.reviewMixability || ''],
    ['effectivenessScore', i18nText.reviewEffectiveness || ''],
    ['goesWellWith', i18nText.reviewGoesWith || ''],
  ])

  const orderFieldByType = reviewFormFields
    .map((field) => {
      return {
        ...field,
        options: field.answerOptions,
        confirmable: undefined,
      }
    })
    .sort((formField1, formField2) => {
      return formField1.type === 'NUMBER'
        ? -1
        : formField2.type === 'NUMBER'
        ? 1
        : 0
    })

  const formFields = orderFieldByType.map((field) => {
    const fieldLabel =
      field.type === 'FEATURERATING'
        ? field.label || field.name || ''
        : chooseLabel.get(field.name) || field.name

    switch (field.type) {
      case 'TEXT':
        return field.name === 'reviewContent'
          ? {
              fieldName: field.name,
              renderField: (ref, key) => (
                <FormItem bindref={ref} key={key} offset={1}>
                  <StyledTextArea
                    value={field.defaultValue || undefined}
                    label={fieldLabel}
                    required={field.required}
                  />
                </FormItem>
              ),
            }
          : {
              fieldName: field.name,
              renderField: (ref, key) => (
                <FormItem bindref={ref} key={key} offset={1}>
                  <Input
                    value={field.defaultValue || undefined}
                    label={fieldLabel}
                    required={field.required}
                    type="text"
                  />
                </FormItem>
              ),
            }
      case 'LIST':
        const fieldPlaceholder = choosePlaceholder.get(field.name) || field.name
        return {
          fieldName: field.name,
          renderField: (ref, key) => (
            <FormItem bindref={ref} key={key} offset={1}>
              <Dropdown
                label={fieldLabel}
                labelHidden={false}
                required={field.required}
                ariaLabel={fieldPlaceholder}
                placeholder={fieldPlaceholder}
                defaultValue={field.defaultValue || undefined}
              >
                {field.options?.map((option, index) => (
                  <DropdownOption
                    value={option.optionKey}
                    key={index}
                    data-testid={`${option.optionKey}-dropdown-option`}
                  >
                    {option?.translation}
                  </DropdownOption>
                ))}
              </Dropdown>
            </FormItem>
          ),
        }
      case 'NUMBER':
        if (useSingleStarRatings) {
          return {
            fieldName: field.name,
            renderField: (ref, key) => (
              <StarRatingPicker
                label={fieldLabel}
                key={key}
                bindref={ref}
                required={field.required}
                starRating={field.defaultValue || undefined}
                screenReaderOnlyText={fieldLabel}
                size={RatingSize.Large}
              />
            ),
          }
        }
        return {
          fieldName: field.name,
          renderField: (ref, key) => (
            <StyledRadioGroup
              required={field.required}
              label={fieldLabel}
              labelHidden={false}
              columns={5}
              horizontal={true}
              removeGap={true}
              offset={1}
              bindref={ref}
              key={key}
              noWrap={true}
            >
              {['1', '2', '3', '4', '5'].map((rating) => {
                return (
                  <StarRatingRadioInput
                    name={fieldLabel}
                    value={rating}
                    key={rating}
                  />
                )
              })}
            </StyledRadioGroup>
          ),
        }
      case 'FEATURERATING':
        if (
          enableReviewFormFeatureRatings &&
          enableOrderReviewsFeatureRatings
        ) {
          return {
            fieldName: field.name,
            renderField: (ref, key) => (
              <StyledFeatureRating
                rating={field.defaultValue || undefined}
                ratingType={field.ratingType}
                screenReaderOnlyText={fieldLabel}
                label={fieldLabel}
                required={field.required}
                measurementLabels={field.answerOptions?.map(
                  (item) => item.optionKey,
                )}
                key={key}
                bindref={ref}
              />
            ),
          }
        }
        return {
          fieldName: '',
          renderField: () => <React.Fragment></React.Fragment>,
        }
      default:
        return {
          fieldName: field.name,
          renderField: (ref, key) => (
            <FormItem bindref={ref} key={key} offset={1}>
              <Input label={field.name} required type="text" />
            </FormItem>
          ),
        }
    }
  })

  return formFields
}
